import { Controller } from '@hotwired/stimulus'

// 口コミ型LP（review_variant）専用コントローラー。
// PC/SP両方のエントリポイント（site_desktop / site_mobile）で読み込まれるよう common/ に配置する。
// モック（design_mocks/review_site/facility_detail.html）内の<script>群を集約していたが、
// #13886 のコンポーネント化で共有部品ぶんは ViewComponent 側の controller へ移管済み
// （口コミカードのクランプ処理は parts/facility/review_site/review_card へ、
// 目次タブのスムーズスクロール・ハイライトは parts/facility/review_site/tabs へ）。
// 残っているのはページ側DOMの制御のみ:
// AI要約（カルーセル・緑ヘッダーのⓘトグル）・Q&Aカルーセル・Q&Aナビ（回答8行クランプ含む）・ツールチップ。
//
// NOTE: connect()はDOM生成・リスナー登録を伴い冪等ではない。現状Turbo等の再接続経路は無いが、
// A-1のViewComponent化で再接続があり得る構成に移す際は冪等化が必要（生成済みボタンの存在チェックは実装済み）
export default class extends Controller {
  initialize () {
    this.documentClickHandlers = []
  }

  connect () {
    this.setupCarousel({
      listSelector: '.rv-ai-summary__boxes',
      cardSelector: '[data-ai-card-index]',
      dotSelector: '[data-ai-index]',
      dotIndexAttr: 'data-ai-index',
      activeClass: 'rv-ai-summary__dot--active',
      initialIndex: 1,
    })
    this.setupCarousel({
      listSelector: '.rv-qa2-list',
      cardSelector: '[data-qa-card-index]',
      dotSelector: '.rv-qa2-dot[data-qa-index]',
      dotIndexAttr: 'data-qa-index',
      activeClass: 'rv-qa2-dot--active',
    })
    this.setupQaNav()
    this.setupFeaturedAnswerClamp()
    this.setupTooltips()
  }

  disconnect () {
    this.documentClickHandlers.forEach((handler) => {
      document.removeEventListener('click', handler)
    })
    this.documentClickHandlers = []
  }

  // AI要約の緑ヘッダーをⓘボタンで開閉する（初期は rv-ai-summary--header-hidden で非表示）
  toggleSummaryHeader (event) {
    const summary = this.element.querySelector('.rv-ai-summary')
    if (!summary) { return }
    const shouldHide = !summary.classList.contains('rv-ai-summary--header-hidden')
    summary.classList.toggle('rv-ai-summary--header-hidden', shouldHide)
    event.currentTarget.setAttribute('aria-expanded', String(!shouldHide))
  }

  // 横スクロールカルーセルとドットの双方向連動（AI要約・qa2で共用）。
  // ドット押下でカードをセンタリングし、スクロール位置から最寄りカードのドットを点灯する。
  setupCarousel ({ listSelector, cardSelector, dotSelector, dotIndexAttr, activeClass, initialIndex }) {
    const list = this.element.querySelector(listSelector)
    if (!list) { return }
    const cards = Array.from(this.element.querySelectorAll(cardSelector))
    const dots = Array.from(this.element.querySelectorAll(dotSelector))
    if (!cards.length) { return }
    let frameRequested = false

    const setActive = (index) => {
      dots.forEach((dot) => {
        const isActive = Number(dot.getAttribute(dotIndexAttr)) === index
        // toggleAttribute は aria-current="" を出力するが、ARIAのtoken型では空文字が
        // 既定値 false に倒れて「現在位置」として読み上げられない。
        // ビュー側の初期出力・setupQaNav と同じ 'true' / 属性削除 に揃える
        if (isActive) {
          dot.setAttribute('aria-current', 'true')
        } else {
          dot.removeAttribute('aria-current')
        }
        dot.classList.toggle(activeClass, isActive)
      })
    }

    const showCard = (index, behavior) => {
      const card = cards[index]
      if (!card) { return }
      const left = card.offsetLeft - (list.clientWidth - card.offsetWidth) / 2
      list.scrollTo({ left: Math.max(0, left), behavior: behavior || 'smooth' })
      setActive(index)
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        showCard(Number(dot.getAttribute(dotIndexAttr)), 'smooth')
      })
    })

    list.addEventListener('scroll', () => {
      if (frameRequested) { return }
      frameRequested = true
      window.requestAnimationFrame(() => {
        const listRect = list.getBoundingClientRect()
        const center = listRect.left + listRect.width / 2
        let closestIndex = 0
        let closestDistance = Infinity
        cards.forEach((card, index) => {
          const rect = card.getBoundingClientRect()
          const distance = Math.abs(rect.left + rect.width / 2 - center)
          if (distance < closestDistance) {
            closestDistance = distance
            closestIndex = index
          }
        })
        setActive(closestIndex)
        frameRequested = false
      })
    }, { passive: true })

    if (initialIndex != null && cards[initialIndex]) {
      // 初期表示は指定カードをセンタリング（レイアウト確定後に実行するため2段rAF）
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => { showCard(initialIndex, 'auto') })
      })
    } else {
      setActive(0)
    }
  }

  // Q&Aナビ（気になる質問から読む）: ナビ押下で対応する回答をアクティブ化してスクロール
  setupQaNav () {
    const navSection = this.element.querySelector('.rv-card--qa-nav')
    if (!navSection) { return }
    const navControls = Array.from(navSection.querySelectorAll('.rv-qa2-nav__item[data-qa-index]'))
    const answers = Array.from(navSection.querySelectorAll('[data-qa-featured-index]'))

    const setNavActive = (index) => {
      navControls.forEach((control) => {
        const isActive = Number(control.getAttribute('data-qa-index')) === index
        if (isActive) {
          control.setAttribute('aria-current', 'true')
        } else {
          control.removeAttribute('aria-current')
        }
      })
      answers.forEach((answer) => {
        answer.classList.toggle('rv-qa-featured--active', Number(answer.getAttribute('data-qa-featured-index')) === index)
      })
    }

    navControls.forEach((control) => {
      control.addEventListener('click', () => {
        const index = Number(control.getAttribute('data-qa-index'))
        const answer = answers[index]
        if (!answer) { return }
        setNavActive(index)
        answer.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    })

    setNavActive(0)
  }

  // Q&Aナビの回答: 8行クランプし、あふれる場合のみ「もっと見る」ボタンを生成して開閉
  setupFeaturedAnswerClamp () {
    const answers = Array.from(this.element.querySelectorAll('.rv-qa-featured__answer'))
    answers.forEach((answer, index) => {
      // 再接続時のボタン重複を防ぐ（connect冪等化の最低限）
      const sibling = answer.nextElementSibling
      if (sibling && sibling.classList.contains('rv-qa-featured__more-button')) { return }

      answer.classList.add('rv-qa-featured__answer--clamped')
      if (answer.scrollHeight <= answer.clientHeight + 1) {
        answer.classList.remove('rv-qa-featured__answer--clamped')
        return
      }

      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'rv-qa-featured__more-button rv-qa-featured__more-button--visible gtm-tap'
      button.setAttribute('data-gtm-event', `rv::qa_featured_read_more_${index + 1}`)
      button.setAttribute('aria-expanded', 'false')
      const label = document.createElement('span')
      label.textContent = 'もっと見る'
      const iconWrap = document.createElement('i')
      iconWrap.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="6" viewBox="0 0 12 6" aria-hidden="true"><path d="M5.293.707a1,1,0,0,1,1.414,0l3.586,3.586A1,1,0,0,1,9.586,6H2.414a1,1,0,0,1-.707-1.707Z" fill="#0a82ff"/></svg>'
      button.appendChild(label)
      button.appendChild(iconWrap)

      button.addEventListener('click', () => {
        const isExpanded = button.getAttribute('aria-expanded') === 'true'
        button.setAttribute('aria-expanded', String(!isExpanded))
        answer.classList.toggle('rv-qa-featured__answer--clamped', isExpanded)
        label.textContent = isExpanded ? 'もっと見る' : '閉じる'
      })

      answer.insertAdjacentElement('afterend', button)
    })
  }

  // 総合評価などのツールチップ: クリック/Enter/Spaceで開閉、Escや外側クリックで閉じる
  setupTooltips () {
    const tooltips = Array.from(this.element.querySelectorAll('.rv-review-tooltip'))
    tooltips.forEach((tooltip) => {
      const icon = tooltip.querySelector('.rv-review-tooltip__icon')
      const panel = tooltip.querySelector('.rv-review-tooltip__panel')
      if (!icon || !panel) { return }

      const setOpen = (isOpen) => {
        panel.hidden = !isOpen
        icon.setAttribute('aria-expanded', String(isOpen))
      }

      icon.addEventListener('click', () => { setOpen(panel.hidden) })
      icon.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          setOpen(panel.hidden)
        }
        if (event.key === 'Escape') { setOpen(false) }
      })

      const outsideClickHandler = (event) => {
        if (!tooltip.contains(event.target)) { setOpen(false) }
      }
      document.addEventListener('click', outsideClickHandler)
      this.documentClickHandlers.push(outsideClickHandler)
    })
  }
}
